// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Tree traversal. Accepts a raw parsed Newick node (from parseNewick) and returns the
// traversal data structures with fully annotated node fields.
//
// Node fields set after traversal:
//   id, name (internal only), parent, children, isLeaf,
//   branchLength, totalNodes, totalLeaves, totalSubtreeLength,
//   visibleLeaves, postIndex, preIndex, isCollapsed, isHidden

function getPostorderTraversal(rootNode) {
  const nodes = [];
  const queue = [rootNode];

  while (queue.length) {
    const node = queue.pop();
    if (node.children) {
      Array.prototype.push.apply(queue, node.children);
    }
    nodes.push(node);
  }

  return nodes.reverse();
}

function getPreorderTraversal(rootNode) {
  const nodes = [];
  const queue = [rootNode];

  while (queue.length) {
    const node = queue.shift();
    nodes.push(node);
    if (node.children) {
      Array.prototype.unshift.apply(queue, node.children);
    }
  }

  return nodes;
}

// Rebuild the preorder/postorder arrays and renumber pre/postIndex after a
// child reorder (e.g. rotation). Topology, ids and node stats are unchanged —
// only the visit sequence and the index fields need refreshing.
export function reindexTraversal(rootNode) {
  const postorderTraversal = getPostorderTraversal(rootNode);
  const preorderTraversal = getPreorderTraversal(rootNode);
  for (let i = 0; i < postorderTraversal.length; i++) {
    postorderTraversal[i].postIndex = i;
  }
  for (let i = 0; i < preorderTraversal.length; i++) {
    preorderTraversal[i].preIndex = i;
  }
  return { postorderTraversal, preorderTraversal };
}

export function traverseTree(rootNode, { trimQuotes = true } = {}) {
  const postorderTraversal = getPostorderTraversal(rootNode);
  const preorderTraversal = getPreorderTraversal(rootNode);

  // Detect cladograms (all branch lengths zero) and synthesise unit lengths
  const isCladogram = postorderTraversal.every(
    (x) => (x.branchLength || x.branch_length || 0) === 0,
  );
  if (isCladogram) {
    rootNode.branchLength = 0;
    for (let nodeIndex = 0; nodeIndex < preorderTraversal.length; nodeIndex++) {
      const node = preorderTraversal[nodeIndex];
      if (node.children) {
        for (const child of node.children) {
          child.branchLength = node.branchLength + 1;
        }
      }
    }
  }

  // Bottom-up pass: leaves → root — compute subtree statistics
  for (let nodeIndex = 0; nodeIndex < postorderTraversal.length; nodeIndex++) {
    const node = postorderTraversal[nodeIndex];
    node.postIndex = nodeIndex;
    node.isLeaf = !Array.isArray(node.children);
    node.branchLength = Math.abs(node.branchLength || node.branch_length || 0);
    delete node.branch_length;

    if (node.isLeaf && typeof node.name === "string") {
      node.id = trimQuotes
        ? node.name.trim().replace(/^['"]|['"]$/g, "")
        : node.name;
      delete node.name;
    }

    node.totalNodes = 1;
    node.totalLeaves = 1;
    node.totalSubtreeLength = 0;

    if (!node.isLeaf) {
      node.totalLeaves = 0;
      let totalSubtreeLength = 0;

      for (const child of node.children) {
        node.totalNodes += child.totalNodes;
        node.totalLeaves += child.totalLeaves;
        const childMaxLength = child.totalSubtreeLength + child.branchLength;
        if (childMaxLength > totalSubtreeLength) {
          totalSubtreeLength = childMaxLength;
        }
        child.parent = node;
      }

      node.totalSubtreeLength = totalSubtreeLength;
    }
  }

  // Top-down pass: root → leaves — assign ids, preIndex, visible leaf counts
  const nodeById = {};
  for (let nodeIndex = 0; nodeIndex < preorderTraversal.length; nodeIndex++) {
    const node = preorderTraversal[nodeIndex];
    node.preIndex = nodeIndex;
    if (!node.id) {
      node.id = nodeIndex.toString();
    }
    nodeById[node.id] = node;
    node.visibleLeaves = node.totalLeaves;
    node.isCollapsed = false;
    node.isHidden = false;
  }

  return {
    nodeById,
    rootNode,
    postorderTraversal,
    preorderTraversal,
  };
}
